import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MagnifyingGlass, Trash, PencilSimple, Key, Eye, UploadSimple, IdentificationCard, Images } from "@phosphor-icons/react";
import { BulkImport } from "../components/BulkImport";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api, errMsg, useApi, fmtDate, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, Table, Badge, CredentialsDialog, ExportButtons } from "../components/kit";
import { PersonPhoto, PhotoEditor } from "../components/Photo";
import BulkPhotos from "../components/BulkPhotos";
import { AadhaarInput, AadhaarView, aadhaarError } from "../components/Aadhaar";
import { StudentFeeSummary } from "../components/fees/StudentFeeSummary";

const BLANK = { name: "", class_id: "", roll_no: "", gender: "", dob: "", blood_group: "", address: "", parent_name: "", parent_phone: "", parent_email: "",
  father_name: "", mother_name: "", social_category: "", religion: "", mother_tongue: "", pen: "", aadhaar: "", cwsn: false, cwsn_type: "", bpl: false, ews_rte: false, repeater: false };
const UDISE_KEYS = ["father_name", "mother_name", "social_category", "religion", "mother_tongue", "pen", "cwsn", "cwsn_type", "bpl", "ews_rte", "repeater"];

function StudentForm({ open, setOpen, classes, initial, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState(initial ? { ...BLANK, ...initial, aadhaar: "" } : BLANK);
  const [dropAadhaar, setDropAadhaar] = useState(false);
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (aadhaarError(f.aadhaar)) return toast.error(aadhaarError(f.aadhaar));
    setBusy(true);
    const body = { ...f, roll_no: f.roll_no ? Number(f.roll_no) : null };
    try {
      if (editing) {
        const { parent_email, ...rest } = body;
        await api.put(`/students/${initial.id}`, { name: rest.name, class_id: rest.class_id, roll_no: rest.roll_no, gender: rest.gender, dob: rest.dob, blood_group: rest.blood_group, address: rest.address, parent_name: rest.parent_name, parent_phone: rest.parent_phone, ...Object.fromEntries(UDISE_KEYS.map((k) => [k, rest[k] ?? BLANK[k]])), ...(rest.aadhaar ? { aadhaar: rest.aadhaar } : dropAadhaar ? { aadhaar: "" } : {}) });
        toast.success("Student updated");
        onSaved(null);
      } else {
        const r = await api.post("/students", body);
        toast.success(`Admitted ${r.data.student.name} · ${r.data.student.admission_no}`);
        onSaved(r.data.credentials);
      }
      setOpen(false);
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, label, props = {}) => (
    <Field label={label}><input className="field" value={f[k] ?? ""} onChange={set(k)} data-testid={`student-${k}-input`} {...props} /></Field>
  );
  return (
    <Modal open={open} onOpenChange={setOpen} title={editing ? t("Edit") : t("New admission")} wide testid="student-form-dialog"
      description={editing ? "" : "Student and parent logins are created automatically."}>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {inp("name", t("Name"), { required: true })}
        <Field label={t("Class")}>
          <select className="field" required value={f.class_id} onChange={set("class_id")} data-testid="student-class-select">
            <option value="">Select class</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
          </select>
        </Field>
        {inp("roll_no", t("Roll no"), { type: "number", min: 1 })}
        <Field label="Gender">
          <select className="field" value={f.gender} onChange={set("gender")} data-testid="student-gender-select">
            <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
          </select>
        </Field>
        {inp("dob", "Date of birth", { type: "date" })}
        {inp("blood_group", "Blood group")}
        {inp("address", "Address")}
        {inp("parent_name", `${t("Parent")} ${t("Name").toLowerCase()}`)}
        {inp("parent_phone", `${t("Parent")} ${t("Phone").toLowerCase()} (WhatsApp)`)}
        {!editing && inp("parent_email", `${t("Parent")} ${t("Email").toLowerCase()} (optional)`, { type: "email" })}
        <div className="sm:col-span-2 eyebrow pt-2 border-t border-white/5">UDISE+ details</div>
        {inp("father_name", "Father's name")}
        {inp("mother_name", "Mother's name")}
        <Field label="Social category">
          <select className="field" value={f.social_category} onChange={set("social_category")} data-testid="student-social_category-select">
            <option value="">—</option>{["General", "SC", "ST", "OBC"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Religion">
          <select className="field" value={f.religion} onChange={set("religion")} data-testid="student-religion-select">
            <option value="">—</option>{["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Parsi", "Jain", "Others"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        {inp("mother_tongue", "Mother tongue")}
        {inp("pen", "PEN (Permanent Education Number)")}
        <AadhaarInput value={f.aadhaar || ""} onChange={(v) => setF({ ...f, aadhaar: v })} last4={dropAadhaar ? "" : initial?.aadhaar_last4}
          onRemove={editing ? () => { if (window.confirm("Remove the saved Aadhaar number when you save?")) setDropAadhaar(true); } : null} />
        <div className="sm:col-span-2 flex flex-wrap gap-5 text-sm text-zinc-300">
          {[["cwsn", "CWSN (special needs)"], ["bpl", "BPL family"], ["ews_rte", "RTE / EWS admission"], ["repeater", "Repeating this class"]].map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-blue-500" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`student-${k}-checkbox`} /> {l}</label>
          ))}
        </div>
        {f.cwsn && (
          <Field label="Type of disability">
            <select className="field" value={f.cwsn_type || ""} onChange={set("cwsn_type")} data-testid="student-cwsn_type-select">
              <option value="">—</option>{["Visual", "Hearing", "Locomotor", "Intellectual", "Multiple", "Others"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        )}
        <button className="btn-primary sm:col-span-2" disabled={busy} data-testid="student-submit-btn">{busy ? "…" : editing ? t("Save") : t("New admission")}</button>
      </form>
    </Modal>
  );
}

function StudentDetail({ id, onClose, isAdmin, onEdit, onCreds, onDeleted }) {
  const { t } = useT();
  const { data: s, reload } = useApi(id ? `/students/${id}` : null);
  const { data: att } = useApi(id ? `/attendance/student/${id}` : null);
  const { data: inv } = useApi(id && isAdmin ? `/fees/student/${id}/summary` : null);
  const del = async () => {
    if (!window.confirm("Remove this student and their logins?")) return;
    try {
      await api.delete(`/students/${id}`);
      toast.success("Student removed");
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg bg-[#07070d] border-white/10 text-white overflow-y-auto" data-testid="student-detail-sheet">
        {!s ? <Loader /> : (
          <>
            <SheetHeader>
              <div className="eyebrow">{s.admission_no} · {s.class_name}</div>
              <SheetTitle className="font-display text-3xl text-white text-left">{s.name}</SheetTitle>
            </SheetHeader>
            <div className="mt-5">
              {s.can_edit_photo ? <PhotoEditor kind="student" person={s} onChange={reload} testid="student-photo" />
                : <PersonPhoto kind="student" person={s} className="h-20 w-20 text-2xl" testid="student-photo-preview" />}
              {s.can_edit_photo && <Link to={`/id-cards?student_id=${s.id}`} className="btn-ghost mt-3 !py-1.5 !px-3" data-testid="student-id-card-btn"><IdentificationCard size={15} /> Print ID card</Link>}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="rounded-2xl bg-white/5 p-4"><div className="eyebrow !text-[10px]">{t("Attendance")}</div><div className="mt-1 font-display text-2xl text-emerald-300">{att?.percent != null ? `${att.percent}%` : "—"}</div></div>
              <div className="rounded-2xl bg-white/5 p-4 col-span-2"><div className="eyebrow !text-[10px]">{t("Fee due")}</div><div className="mt-1 font-display text-2xl text-orange-300">{inv ? inr(inv.total.balance) : "—"}</div></div>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
              {[["Roll no", s.roll_no], ["Gender", s.gender], ["DOB", s.dob], ["Blood group", s.blood_group], ["Parent", s.parent_name], ["Phone", s.parent_phone],
                ["Admitted", fmtDate(s.admission_date)], ["Status", s.status], ["Aadhaar", <AadhaarView sid={s.id} masked={s.aadhaar_masked} isAdmin={isAdmin} />], ["Address", s.address]].map(([k, v]) => (
                <div key={k} className={k === "Address" ? "col-span-2" : ""}><dt className="eyebrow !text-[10px]">{t(k)}</dt><dd className="mt-1 text-zinc-200">{v || "—"}</dd></div>
              ))}
            </dl>
            {isAdmin && inv && <div className="mt-6"><div className="eyebrow !text-[10px] mb-2">Fees by type</div><StudentFeeSummary summary={inv} compact testid="detail-fee-summary" /></div>}
            {isAdmin && (
              <div className="mt-6 rounded-2xl bg-white/5 p-4 text-sm space-y-1">
                <div className="eyebrow !text-[10px] mb-2">Logins</div>
                <div className="text-zinc-400">Student: <span className="text-zinc-200" data-testid="detail-student-login">{s.student_login}</span></div>
                <div className="text-zinc-400">Parent: <span className="text-zinc-200" data-testid="detail-parent-login">{s.parent_login}</span></div>
              </div>
            )}
            {inv?.length > 0 && (
              <div className="mt-6 space-y-2">
                <div className="eyebrow">{t("Invoices")}</div>
                {inv.map((i) => (
                  <div key={i.id} className="flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2 text-sm">
                    <span>{i.title}</span><span className="flex items-center gap-2">{inr(i.amount)} <Badge status={i.status} /></span>
                  </div>
                ))}
              </div>
            )}
            {isAdmin && (
              <div className="mt-8 flex flex-wrap gap-2">
                <button className="btn-ghost" onClick={() => onEdit(s)} data-testid="student-edit-btn"><PencilSimple size={16} /> {t("Edit")}</button>
                <button className="btn-ghost" onClick={onCreds} data-testid="student-reset-creds-btn"><Key size={16} /> Reset logins</button>
                <button className="btn-ghost !text-rose-300" onClick={del} data-testid="student-delete-btn"><Trash size={16} /> {t("Delete")}</button>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Students() {
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user.role === "admin";
  const [cls, setCls] = useState("");
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [bulk, setBulk] = useState(false);
  const [photos, setPhotos] = useState(false);
  const { data: classes } = useApi("/classes");
  const { data, loading, reload } = useApi(`/students?class_id=${cls}&q=${encodeURIComponent(q)}`);
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState(null);
  const [view, setView] = useState(null);
  const cmap = Object.fromEntries((classes || []).map((c) => [c.id, `${c.name} ${c.section}`]));
  const resetCreds = async () => {
    try {
      const r = await api.post(`/students/${view}/credentials`);
      setCreds(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Records" title={t("Students")} subtitle={`${data?.length ?? 0} students${cls ? ` in ${cmap[cls]}` : ""}`}
        actions={<><Link to={`/id-cards${cls ? `?class_id=${cls}` : ""}`} className="btn-ghost" data-testid="id-cards-btn"><IdentificationCard size={16} /> ID cards</Link>{isAdmin && (<><ExportButtons entity="students" /><button className="btn-ghost" onClick={() => setPhotos(true)} data-testid="bulk-photos-btn"><Images size={16} /> Bulk photos</button><button className="btn-ghost" onClick={() => setBulk(true)} data-testid="bulk-import-btn"><UploadSimple size={16} /> Bulk import</button><button className="btn-primary" onClick={() => setForm({})} data-testid="new-admission-btn"><Plus size={16} weight="bold" /> {t("New admission")}</button></>)}</>} />
      <GlassCard className="p-4 sm:p-6">
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input className="field !pl-10" placeholder="Search name / admission no" value={q} onChange={(e) => setQ(e.target.value)} data-testid="student-search-input" />
          </div>
          <select className="field sm:!w-56" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="student-class-filter">
            <option value="">{t("All classes")}</option>
            {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
          </select>
        </div>
        {loading ? <Loader /> : !data?.length ? <Empty hint="Admit your first student to get started." /> : (
          <Table testid="students-table">
            <thead><tr><th>{t("Admission no")}</th><th>{t("Name")}</th><th>{t("Class")}</th><th>{t("Roll no")}</th><th>{t("Parent")}</th><th>{t("Phone")}</th><th></th></tr></thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} data-testid={`student-row-${s.admission_no}`}>
                  <td className="font-mono text-xs text-zinc-400">{s.admission_no}</td>
                  <td className="font-semibold text-white">{s.name}</td>
                  <td>{cmap[s.class_id]}</td><td>{s.roll_no ?? "—"}</td><td>{s.parent_name || "—"}</td><td>{s.parent_phone || "—"}</td>
                  <td className="text-right"><button className="btn-ghost !py-1.5 !px-3" onClick={() => setView(s.id)} data-testid={`student-view-${s.admission_no}`}><Eye size={15} /> {t("View")}</button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </GlassCard>
      {form && <StudentForm open={!!form} setOpen={(o) => !o && setForm(null)} classes={classes || []} initial={form.id ? form : null}
        onSaved={(c) => { reload(); if (c) setCreds(c); if (form.id) setView(null); }} />}
      <StudentDetail id={view} onClose={() => setView(null)} isAdmin={isAdmin} onEdit={(s) => { setView(null); setForm({ ...s, roll_no: s.roll_no ?? "" }); }}
        onCreds={resetCreds} onDeleted={() => { setView(null); reload(); }} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
      {isAdmin && <BulkImport open={bulk} setOpen={setBulk} onDone={reload} />}
      {isAdmin && <BulkPhotos open={photos} setOpen={setPhotos} onDone={reload} />}
      {!isAdmin && <p className="mt-4 text-xs text-zinc-500">Read-only view. <Link to="/attendance" className="text-blue-300">{t("Mark attendance")}</Link></p>}
    </div>
  );
}
